import React, { useCallback, useContext, useEffect, useState } from 'react';
import * as QRCode from 'qrcode';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { BUTTON_AS_TYPE, INFOBOX_VARIANT } from '@motorway/mw-highway-code/enums';
import { toast } from '@motorway/mw-highway-code/pre-alpha';
import { useDecision } from '@optimizely/react-sdk';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { VERIFICATION_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsDocumentVerification';
import { FLAG } from 'Utilities/analytics/optimizely/constants';
import { isDesktopDevice } from 'Utilities/devices';
import { safeLocalStorage } from 'Utilities/storages';

import ConfirmModal from 'Components/ConfirmModal/ConfirmModal';
import { StepList } from 'Storybook/components/local/StepList';

import {
  DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY,
  DOCUMENT_KINDS,
} from '../../DocumentCapture.helpers';
import { DocumentCaptureOfferContext } from '../../DocumentCaptureContexts';
import {
  HowVerificationWorksModal,
} from '../../HowVerificationWorksModal/HowVerificationWorksModal';
import { ExampleDocuments } from '../ExampleDocuments/ExampleDocuments';
import { EXAMPLE_DOCUMENTS } from '../ExampleDocuments/ExampleDocuments.helpers';
import { useIncodeVerificationUrl } from '../hooks/useIncodeVerificationUrl';
import { useOneTimeToken } from '../hooks/useOneTimeToken';
import LocalTexts from '../UploadDocuments.texts.json';

import styles from './UploadDocumentsVerification.scss';

const LocalT = new MDText(LocalTexts.uploadDocumentsVerification);

const steps = [
  {
    content: <p data-thc-style-override>
      {LocalT.translate('steps.snapYourDrivingLicence', {
        drivingLicence: <span className={styles.exampleDocLink}>
          <ExampleDocuments
            {
              ...EXAMPLE_DOCUMENTS[
                DOCUMENT_KINDS.DRIVING_LICENCE as keyof typeof EXAMPLE_DOCUMENTS
              ] as Record<string, unknown>
            }
            actionLink={LocalT.translate('steps.drivingLicence') as string}
            documentTitle={LocalT.translate('steps.drivingLicenceModalTitle') as string}
            modalDescription={LocalT.translate('steps.drivingLicenceModalDescription') as string}
          />
        </span>,
      })}
    </p>,
  },
  {
    content: LocalT.translate('steps.takeASelfie'),
  },
  {
    content: <p data-thc-style-override>
      {LocalT.translate('steps.uploadYourV5', {
        v5: <span className={styles.exampleDocLink}>
          <ExampleDocuments
            {...EXAMPLE_DOCUMENTS[DOCUMENT_KINDS.V5 as keyof typeof EXAMPLE_DOCUMENTS] as Record<string, unknown>}
            actionLink={LocalT.translate('steps.v5c') as string}
            documentTitle={LocalT.translate('steps.v5c') as string}
            modalDescription={LocalT.translate('steps.v5cModalDescription') as string}
          />
        </span>,
      })}
    </p>,
  },
];

export const UploadDocumentsVerification = () => {
  const { id: dpEnquiryId } = useContext(DocumentCaptureOfferContext) || {};
  const { userState: user } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSafeDesktopDevice, setIsSafeDesktopDevice] = useState(false);

  const { token } = useOneTimeToken();

  const authToken = user?.auth_token;

  const onError = useCallback(() => {
    toast({
      content: LocalT.translate('errorContent'),
      heading: LocalT.translate('errorHeading'),
      id: 'incode-verification-error',
      variant: INFOBOX_VARIANT.ERROR,
    });
  }, []);

  const [{ enabled, variationKey }] = useDecision(FLAG.INCODE_SDK_FLOW, {}, {
    overrideUserId: user?.dpSellerId?.toString(),
  });
  const useIncodeSDKFlow = enabled && variationKey === 'on';

  const { incodeUrl, loading } = useIncodeVerificationUrl({
    authToken,
    dpEnquiryId,
    incodeSDKFlow: useIncodeSDKFlow,
    onError,
  });

  useEffect(() => {
    if (!loading) {
      VERIFICATION_ANALYTICS_EVENTS.LOAD_HANDOFF();
      setIsSafeDesktopDevice(isDesktopDevice());
    }
  }, [loading]);

  useEffect(() => {
    const reviewStatusViewVehicles = safeLocalStorage.getItem(DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY) || [];
    const currentEnquryId = JSON.stringify(dpEnquiryId);

    if (reviewStatusViewVehicles.includes(currentEnquryId) || !dpEnquiryId) {
      return;
    }

    reviewStatusViewVehicles.push(currentEnquryId);
    safeLocalStorage.setItem(DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY, reviewStatusViewVehicles);
  });

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const buttonProps = () => {
    if (isSafeDesktopDevice && useIncodeSDKFlow) {
      return {
        onClick: () => setIsModalOpen(true),
      };
    }

    return {
      as: BUTTON_AS_TYPE.ANCHOR,
      disabled: loading,
      href: useIncodeSDKFlow || !incodeUrl ? `/${vehicle.vrm}/automated-docs-upload` : incodeUrl as string,
      loading,
      onClick: VERIFICATION_ANALYTICS_EVENTS.CONTINUE_VERIFICATION,
    };
  };

  return (
    <div>
      {isModalOpen && (
        <ConfirmModal
          confirmText={LocalT.translate('incodeQrModal.confirm') as string}
          modalClassName={styles.confirmModal}
          onConfirm={closeModal}
          onReject={closeModal}
        >
          <canvas
            ref={(node) => {
              if (node) {
                const qrUrl = `${window.location.origin}/${vehicle.vrm}/automated-docs-upload${token ? `?token=${token}` : ''}`;
                QRCode.toCanvas(node, qrUrl);
              }
            }}
          />
          <h2>{LocalT.translate('incodeQrModal.title')}</h2>
          <p>{LocalT.translate('incodeQrModal.content')}</p>
        </ConfirmModal>
      )}
      <div className={styles.uploadDocumentsVerification}>
        <div className={styles.summary}>
          <h2 className={styles.title}>{LocalT.translate('documentVerification')}</h2>
          <p data-thc-style-override className={styles.info}>{LocalT.translate('documentVerificationDescription')}</p>
          <HowVerificationWorksModal/>
        </div>
        <div className={styles.steps}>
          <h5 className={styles.stepsTitle}>{LocalT.translate('steps.just3Steps')}</h5>
          <StepList align="center" steps={steps}/>
        </div>
        <div className={styles.buttonContainer}>
          <Button
            fullWidth
            label={LocalT.translate('continueWithVerification')}
            loading={loading}
            { ...buttonProps() }
          />
          <p data-thc-style-override className={styles.info}>{LocalT.translate('secureRedirect')}</p>
        </div>
      </div>
    </div>
  );
};
